/**
 * Root Bible state — extends BibleStateCore with study, guide, and import/export.
 *
 * Core navigation/display/UI/chat/user-data delegation lives in bible-state-core.ts.
 * Import/export logic lives in mobile-import-export.ts.
 * Guide helpers live in bible-guide-helpers.ts.
 */

import { makeObservable } from 'mobx';
import type {
  BibleLocation,
  Study,
  StudyGroup,
  StudentAddOptions,
  WorkingStudy,
} from '@skjv/common';
import type { ThemeMode, ColorTheme, StrongsDisplay } from './display';
import type { StudySettingsTab, StudyEditorMode, ImportResult } from './study';
import { StudyState, StudyUiState } from './study';
import { GuideState } from './guide';
import { StudyIndexState } from './study-index-state';
import { BibleStateCore } from './bible-state-core';
import { importMobileData, type MobileImportResult } from './mobile-import';
import { exportUserData } from './mobile-export';
import * as guideHelpers from './bible-guide-helpers';
import { isDesktop } from '@/desktop/desktop';

// Re-export types for backward compatibility
export type { ThemeMode, ColorTheme, StrongsDisplay };

class BibleState extends BibleStateCore {
  private readonly studyStore = new StudyState();
  private readonly studyUiStore = new StudyUiState();
  private readonly guideStore = new GuideState();
  readonly studyIndex = new StudyIndexState();

  constructor() {
    super();
    makeObservable(this);
    // The Drive study-index poller serves the Study system, which is
    // native-only — the website must not poll for it.
    if (typeof window !== 'undefined' && isDesktop()) this.studyIndex.start();
  }

  // ── UI Panel Override ───────────────────────────────────────

  hideAllPanels(): void {
    this.ui.hideAllPanels();
    this.studyUiStore.hideStudyPanel();
  }

  // ── Studies ─────────────────────────────────────────────────

  get studies(): Study[] { return this.studyStore.studies; }
  get isLibraryLoading(): boolean { return this.studyStore.isLibraryLoading; }
  get sortedStudies(): Study[] { return this.studyStore.sortedStudies; }
  get studyGroups(): StudyGroup[] { return this.studyStore.studyGroups; }
  get sortedStudyGroups(): StudyGroup[] { return this.studyStore.sortedStudyGroups; }
  get selectedStudy(): Study | undefined { return this.studyStore.selectedStudy; }
  get selectedStudyId(): string | null { return this.studyStore.selectedStudyId; }

  selectStudy(id: string | null): void { this.studyStore.selectStudy(id); }
  addStudy(study: Omit<Study, 'id' | 'createdAt' | 'updatedAt'>): string { return this.studyStore.addStudy(study); }
  updateStudy(id: string, updates: Partial<Omit<Study, 'id' | 'createdAt'>>): void { this.studyStore.updateStudy(id, updates); }
  removeStudy(id: string): void { this.studyStore.removeStudy(id); }
  duplicateStudy(id: string): string | null { return this.studyStore.duplicateStudy(id); }

  addStudyGroup(name: string, description?: string, leaderCount?: number): string { return this.studyStore.addStudyGroup(name, description, leaderCount); }
  updateStudyGroup(id: string, updates: Partial<Omit<StudyGroup, 'id' | 'students'>>): void { this.studyStore.updateStudyGroup(id, updates); }
  removeStudyGroup(id: string): void { this.studyStore.removeStudyGroup(id); }
  getStudyGroup(id: string): StudyGroup | undefined { return this.studyStore.getStudyGroup(id); }

  addStudentToGroup(groupId: string, student: StudentAddOptions): string | null { return this.studyStore.addStudentToGroup(groupId, student); }
  updateStudent(groupId: string, studentId: string, updates: Partial<Omit<import('@skjv/common').Student, 'id'>>): void { this.studyStore.updateStudent(groupId, studentId, updates); }
  removeStudentFromGroup(groupId: string, studentId: string): void { this.studyStore.removeStudentFromGroup(groupId, studentId); }
  addTeacherToGroup(groupId: string, name: string): string | null { return this.studyStore.addTeacherToGroup(groupId, name); }
  removeTeacherFromGroup(groupId: string, teacherId: string): void { this.studyStore.removeTeacherFromGroup(groupId, teacherId); }
  createAssignmentsForGroup(groupId: string): import('@skjv/common').StudentAssignment[] { return this.studyStore.createAssignmentsForGroup(groupId); }
  setStudyGroupMemoryStart(groupId: string, location: BibleLocation | null): void { this.studyStore.setStudyGroupMemoryStart(groupId, location); }
  setStudyGroupMemoryEnd(groupId: string, location: BibleLocation | null): void { this.studyStore.setStudyGroupMemoryEnd(groupId, location); }

  // ── Study Editing ───────────────────────────────────────────

  get editingStudy(): WorkingStudy | null { return this.studyStore.editingStudy; }
  startNewStudy(): void { this.studyStore.startNewStudy(); }
  loadStudyForEditing(id: string): void { this.studyStore.loadStudyForEditing(id); }
  updateEditingStudy(updates: Partial<WorkingStudy>): void { this.studyStore.updateEditingStudy(updates); }
  clearEditingStudy(): void { this.studyStore.clearEditingStudy(); }
  saveEditingStudy(): string | null { return this.studyStore.saveEditingStudy(); }
  exportStudyToJson(id: string): string | null { return this.studyStore.exportStudyToJson(id); }
  exportAllStudiesToJson(): string { return this.studyStore.exportAllToJson(); }
  importStudiesFromJson(jsonData: string): ImportResult { return this.studyStore.importFromJson(jsonData); }
  importRawStudyWithGroup(data: Record<string, unknown>, groupName: string): { studiesCount: number; groupsCount: number } { return this.studyStore.importRawStudyWithGroup(data, groupName); }
  bulkImportStudies(studies: Study[], groups: StudyGroup[]): Promise<void> { return this.studyStore.bulkImport(studies, groups); }

  // ── Study UI ────────────────────────────────────────────────

  get studyPanelVisible(): boolean { return this.studyUiStore.studyPanelVisible; }
  get studyCloseNonce(): number { return this.studyUiStore.studyCloseNonce; }
  get studySettingsOpen(): boolean { return this.studyUiStore.studySettingsOpen; }
  get studySettingsTab(): StudySettingsTab { return this.studyUiStore.studySettingsTab; }
  get studyEditorMode(): StudyEditorMode { return this.studyUiStore.editorMode; }
  get editingStudyId(): string | null { return this.studyUiStore.editingStudyId; }
  get editingGroupId(): string | null { return this.studyUiStore.editingGroupId; }

  showStudyPanel(): void { this.studyUiStore.showStudyPanel(); }
  hideStudyPanel(): void { this.studyStore.clearEditingStudy(); this.studyUiStore.hideStudyPanel(); }
  openStudySettings(tab?: StudySettingsTab): void { this.studyUiStore.openStudySettings(tab); }
  closeStudySettings(): void { this.studyUiStore.closeStudySettings(); }
  setStudySettingsTab(tab: StudySettingsTab): void { this.studyUiStore.setStudySettingsTab(tab); }
  get expandedGroupId(): string | null { return this.studyUiStore.expandedGroupId; }
  toggleGroupExpansion(id: string): void { this.studyUiStore.toggleGroupExpansion(id); }
  /** Tour: open the accordion of the most recently used (= just created) group */
  expandNewestGroup(): void {
    const [newest] = this.studyStore.sortedStudyGroups;
    if (newest) this.studyUiStore.expandGroup(newest.id);
  }
  startCreateStudy(): void { this.studyStore.startNewStudy(); this.studyUiStore.startCreateStudy(); }
  startEditStudy(id: string): void { this.studyStore.loadStudyForEditing(id); this.studyUiStore.startEditStudy(id); }
  viewStudy(id: string): void { this.studyUiStore.viewStudy(id); }
  backToStudyList(): void { this.studyStore.clearEditingStudy(); this.studyUiStore.backToList(); }
  startEditGroup(id: string): void { this.studyUiStore.startEditGroup(id); }
  stopEditGroup(): void { this.studyUiStore.stopEditGroup(); }

  // ── Guide ───────────────────────────────────────────────────

  get isGuideActive(): boolean { return this.guideStore.isActive; }
  get guideCurrentStep(): number { return this.guideStore.currentStep; }
  get guideTotalSteps(): number { return this.guideStore.totalSteps; }
  get guideStep() { return this.guideStore.step; }
  get guideHighlightSelector(): string | null { return this.guideStore.highlightSelector; }
  get hasSeenGuide(): boolean { return this.guideStore.hasSeenGuide; }
  /** True only while the tour's CURRENT step works inside the Groups &
   *  Students modal — the modal drops its scrim for exactly these steps. */
  get guideDrivesSettings(): boolean {
    return this.guideStore.isActive && !!this.guideStore.step.usesStudySettings;
  }

  startGuide(): void { this.guideStore.startGuide(); this.ui.showUserDataPanel('guide'); }
  guideNextStep(): void { this.guideStore.nextStep(this); }
  guidePrevStep(): void { this.guideStore.prevStep(this); }
  endGuide(): void { this.guideStore.endGuide(); }

  /** Closing the panel mid-tour is a dismissal — mark the tour seen so it
   *  doesn't auto-open again on every launch. */
  override hideUserDataPanel(): void {
    if (this.guideStore.isActive) this.guideStore.endGuide();
    super.hideUserDataPanel();
  }

  get isGuideStepReady(): boolean {
    const readyFn = this.guideStore.step.isReady;
    if (!readyFn) return true;
    return readyFn(this);
  }

  hasAnyGroup(): boolean { return guideHelpers.hasAnyGroup(this.studyStore); }
  hasStudentsInAnyGroup(): boolean { return guideHelpers.hasStudentsInAnyGroup(this.studyStore); }
  hasTeachersInAnyGroup(): boolean { return guideHelpers.hasTeachersInAnyGroup(this.studyStore); }
  isCreatingStudy(): boolean { return guideHelpers.isCreatingStudy(this.studyUiStore); }
  hasEditingStudyStartLocation(): boolean { return guideHelpers.hasEditingStudyStartLocation(this.studyStore); }
  hasEditingStudyEndLocation(): boolean { return guideHelpers.hasEditingStudyEndLocation(this.studyStore); }
  isEditingStudySaved(): boolean { return guideHelpers.isEditingStudySaved(this.studyStore); }

  // ── Import/Export ───────────────────────────────────────────

  importMobileData(jsonData: string): MobileImportResult {
    return importMobileData(jsonData, {
      bookmarksStore: this.bookmarksStore,
      notesStore: this.notesStore,
      prayerStore: this.prayerStore,
      memoryStore: this.memoryStore,
      navigationStore: this.navigationStore,
    });
  }

  exportUserData(): string {
    return exportUserData({
      bookmarks: this.bookmarksStore.bookmarks,
      notes: this.notesStore.notes,
      prayerItems: this.prayerStore.prayerItems,
      bibleHistory: this.navigationStore.bibleHistory,
      memoryVerse: this.memoryStore.memoryVerse,
      memoryVerseFrequency: this.memoryStore.memoryVerseFrequency,
    });
  }
}

export const bibleState = new BibleState();
